/**
 * Pre-Journey Plan setup: the words the setup tab and the report draw for a config, and the two
 * edits that move many groups at once (a new "Group stores by", and a group's values moved
 * between groups). Pure, so the rules are tested here and the tab only draws them.
 *
 * A group covers every combination of its values, one list per group field: match
 * [['outside'], ['Mac', 'Non-Mac']] is the outside-city stores of trade Mac or Non-Mac. No two
 * groups may cover one combination (the server refuses it), so a move either takes a whole
 * group in, trims the values another group gives up, or is refused when it would split one.
 */
import type {
  PlanCondition,
  PlanConfig,
  PlanFieldInfo,
  PlanFieldKey,
  PlanGroupConfig,
  PlanPriorityConfig,
  PlanVisits,
  StoreFieldKey,
} from '../../types/preJourneyPlan';

export const MAX_GROUPS = 60;
export const MAX_PRIORITIES = 20;
export const MAX_CONDITIONS = 5;
export const MAX_GROUP_FIELDS = 2;
export const GROUP_NAME_MAX = 60;
export const PRIORITY_NAME_MAX = 60;
export const ALL_STORES = 'All stores';

/** Values are compared as the server does: case and spacing folded. */
export const fold = (value: string): string => value.trim().replace(/\s+/g, ' ').toLowerCase();

const LOCATION_LABEL: Record<string, string> = { outside: 'Outside city', inside: 'Inside city' };
const LOCATION_VALUES = ['outside', 'inside'];

/** A value as the setup shows it: the location's two keys by name, '' as (blank). */
export const valueLabel = (field: PlanFieldKey, value: string): string => {
  if (field === 'location') return LOCATION_LABEL[value] ?? value;
  return value.trim() === '' ? '(blank)' : value;
};

/** A field by its key, for labels and values. */
export type FieldMap = Map<string, PlanFieldInfo>;
export const fieldMapOf = (lists: PlanFieldInfo[][]): FieldMap => {
  const map: FieldMap = new Map();
  for (const list of lists) for (const f of list) map.set(f.key, f);
  return map;
};
export const fieldLabel = (fields: FieldMap, key: PlanFieldKey): string => fields.get(key)?.label ?? 'Removed field';

const uniqueFolded = (values: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const k = fold(v);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
};

/** The values a group field has, in the order the server lists them; the groups are made from these. */
export const fieldValues = (fields: FieldMap, key: StoreFieldKey): string[] => {
  const listed = fields.get(key)?.values;
  if (key === 'location') return listed && listed.length > 0 ? uniqueFolded(listed) : LOCATION_VALUES;
  return uniqueFolded(listed ?? []);
};

/**
 * What a values picker for one group field offers: the field's values, any value a group holds
 * that the list no longer has, and (blank) for stores with no value, which matches only a group
 * that lists it.
 */
export const valueOptions = (fields: FieldMap, key: StoreFieldKey, groups: PlanGroupConfig[], dim: number): string[] => {
  const held = groups.flatMap((g) => g.match[dim] ?? []);
  const values = uniqueFolded([...fieldValues(fields, key), ...held]);
  if (key !== 'location' && !values.some((v) => v.trim() === '')) values.push('');
  return values;
};

/** The first free id with this prefix: g1, g2, … */
export const nextId = (prefix: string, used: Iterable<string>): string => {
  const taken = new Set(used);
  let n = 1;
  while (taken.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
};

export const clip = (text: string, max: number): string => (text.length > max ? text.slice(0, max).trimEnd() : text);

/** A group's name from its values: 'Outside city', 'Outside city · Mac'. */
export const groupNameOf = (groupBy: StoreFieldKey[], combo: string[]): string =>
  clip(combo.map((v, i) => valueLabel(groupBy[i], v)).join(' · '), GROUP_NAME_MAX);

/** The grey line under a group's name: the values it covers, a long list cut short. */
export const groupValuesLine = (groupBy: StoreFieldKey[], group: PlanGroupConfig): { short: string; full: string } => {
  if (groupBy.length === 0) return { short: 'Every store', full: 'Every store' };
  const parts = groupBy.map((key, i) => {
    const labels = (group.match[i] ?? []).map((v) => valueLabel(key, v));
    const full = labels.join(', ');
    const short = labels.length > 3 ? `${labels.slice(0, 3).join(', ')} +${labels.length - 3} more` : full;
    return { short: short || 'No values', full: full || 'No values' };
  });
  return { short: parts.map((p) => p.short).join(' · '), full: parts.map((p) => p.full).join(' · ') };
};

/** Whether the config reads the person's base city: grouped by, or a condition on, the location. */
export const usesLocation = (config: PlanConfig): boolean =>
  config.groupBy.includes('location')
  || config.groups.some((g) => g.priorities.some((p) => p.conditions.some((c) => c.field === 'location')));

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** The visits box: '2 · every month', '1 · M1, M2', 'M1 2 · M2 1', '1 · quarter'. */
export const visitsLabel = (visits: PlanVisits): string => {
  if (visits.mode === 'per_quarter') return `${visits.count} · quarter`;
  const m = visits.months;
  const on = [0, 1, 2].filter((i) => m[i] > 0);
  if (on.length === 0) return '0';
  if (on.every((i) => m[i] === m[on[0]])) {
    return on.length === 3 ? `${m[0]} · every month` : `${m[on[0]]} · ${on.map((i) => `M${i + 1}`).join(', ')}`;
  }
  return on.map((i) => `M${i + 1} ${m[i]}`).join(' · ');
};

const valuesText = (field: PlanFieldKey, values: string[]): string => {
  const labels = values.map((v) => valueLabel(field, v));
  return labels.length <= 2 ? labels.join(' or ') : `one of ${labels.length}`;
};

/** One condition in a few words: 'Joined within 12 months', 'Category is Mac', '1+ red flags'. */
export const conditionText = (c: PlanCondition, fields: FieldMap): string => {
  const info = fields.get(c.field);
  const label = info?.label ?? 'Removed field';
  const n = typeof c.number === 'number' ? c.number : 0;
  const values = c.values ?? [];
  switch (c.op) {
    case 'within_months':
      return `${label} ${plural(n, 'month', 'months')}`;
    case 'months_running':
      return c.field === 'score_fell' ? `Score fell ${plural(n, 'month', 'months')} running` : `${label} ${plural(n, 'month', 'months')} running`;
    case 'at_least':
      return c.field === 'red_flags' ? `${n.toLocaleString()}+ red ${n === 1 ? 'flag' : 'flags'}` : `${label} at least ${n.toLocaleString()}`;
    case 'at_most':
      return `${label} at most ${n.toLocaleString()}`;
    case 'is_true':
      return info?.kind === 'yesno' ? `${label} is yes` : label;
    case 'is_false':
      return `${label} is no`;
    case 'any_of':
      return c.field === 'location' ? valuesText(c.field, values) : `${label} is ${valuesText(c.field, values)}`;
    case 'none_of':
      return `${label} not ${valuesText(c.field, values)}`;
    default:
      return label;
  }
};

/** A priority's grey line: its first condition, and how many more; the whole of it for the hover. */
export const prioritySummary = (p: PlanPriorityConfig, fields: FieldMap): { short: string; full: string } => {
  const texts = p.conditions.map((c) => conditionText(c, fields));
  if (texts.length === 0) return { short: 'No conditions', full: 'No conditions' };
  return { short: texts.length > 1 ? `${texts[0]} +${texts.length - 1}` : texts[0], full: texts.join(' and ') };
};

const cartesian = (lists: string[][]): string[][] =>
  lists.reduce<string[][]>((acc, list) => acc.flatMap((combo) => list.map((v) => [...combo, v])), [[]]);

const cloneGroup = (g: PlanGroupConfig): PlanGroupConfig => JSON.parse(JSON.stringify(g)) as PlanGroupConfig;

export interface Regrouped {
  groups: PlanGroupConfig[];
  /** Names of the groups whose priorities no new group keeps. */
  lost: string[];
  /** Set when the fields have more value combinations than groups allowed: no group is made. */
  tooMany: number | null;
}

/**
 * The groups for a new "Group stores by": one per value (per value pair with two fields), named
 * from the values. A new group keeps the priorities of the old groups that cover it on the
 * fields both choices share when those groups agree (adding a second field keeps each first-field
 * group's priorities on all its pairs); otherwise it starts with none. `lost` names the old groups
 * whose priorities went nowhere, so the tab can ask first.
 */
export const regroup = (config: PlanConfig, groupBy: StoreFieldKey[], fields: FieldMap): Regrouped => {
  const old = config.groups;
  const used = new Set<string>();
  const common = groupBy
    .map((key, newIndex) => ({ newIndex, oldIndex: config.groupBy.indexOf(key) }))
    .filter((x) => x.oldIndex >= 0);

  const inherit = (combo: string[]): { priorities: PlanPriorityConfig[]; enabled: boolean } => {
    const candidates = old.filter((g) => common.every(({ newIndex, oldIndex }) =>
      (g.match[oldIndex] ?? []).some((v) => fold(v) === fold(combo[newIndex]))));
    if (candidates.length === 0) return { priorities: [], enabled: true };
    const first = JSON.stringify(candidates[0].priorities);
    if (!candidates.every((g) => JSON.stringify(g.priorities) === first)) return { priorities: [], enabled: true };
    for (const g of candidates) used.add(g.id);
    return { priorities: cloneGroup(candidates[0]).priorities, enabled: candidates.some((g) => g.enabled) };
  };

  const finish = (groups: PlanGroupConfig[], tooMany: number | null): Regrouped => ({
    groups,
    lost: old.filter((g) => g.priorities.length > 0 && !used.has(g.id)).map((g) => g.name),
    tooMany,
  });

  if (groupBy.length === 0) {
    const { priorities, enabled } = inherit([]);
    return finish([{ id: 'all', name: ALL_STORES, enabled, match: [], priorities }], null);
  }
  const lists = groupBy.map((key) => fieldValues(fields, key));
  const total = lists.reduce((n, list) => n * list.length, 1);
  if (total > MAX_GROUPS) return finish([], total);

  const ids: string[] = [];
  const groups = cartesian(lists).map((combo) => {
    const id = nextId('g', ids);
    ids.push(id);
    const { priorities, enabled } = inherit(combo);
    return { id, name: groupNameOf(groupBy, combo), enabled, match: combo.map((v) => [v]), priorities };
  });
  return finish(groups, null);
};

export type Placed = { groups: PlanGroupConfig[]; removed: PlanGroupConfig[] } | { error: string };

/**
 * Gives `target` exactly the values `match` holds (one list per group field) and settles the
 * other groups. Merge: a group wholly inside the new values is taken in (removed, its
 * priorities with it), one that shares values on one field only gives those up, and one that
 * would be split in two is refused. Add: the values must belong to no other group.
 */
export const placeGroup = (
  groups: PlanGroupConfig[],
  target: PlanGroupConfig,
  match: string[][],
  mode: 'merge' | 'add',
): Placed => {
  if (match.some((list) => list.length === 0)) return { error: 'Pick at least one value.' };
  const wanted = match.map((list) => new Set(list.map(fold)));
  const next: PlanGroupConfig[] = [];
  const removed: PlanGroupConfig[] = [];
  for (const g of groups) {
    if (g.id === target.id) {
      next.push({ ...target, match });
      continue;
    }
    const overlaps = wanted.every((set, i) => (g.match[i] ?? []).some((v) => set.has(fold(v))));
    if (!overlaps) {
      next.push(g);
      continue;
    }
    if (mode === 'add') return { error: `Some of these are in ${g.name}.` };
    const notInside = wanted
      .map((set, i) => ((g.match[i] ?? []).every((v) => set.has(fold(v))) ? -1 : i))
      .filter((i) => i >= 0);
    if (notInside.length === 0) {
      removed.push(g);
    } else if (notInside.length === 1) {
      const j = notInside[0];
      next.push({ ...g, match: g.match.map((list, i) => (i === j ? list.filter((v) => !wanted[j].has(fold(v))) : list)) });
    } else {
      return { error: `${g.name} would be split. Move all of it or none.` };
    }
  }
  if (mode === 'add') next.push({ ...target, match });
  return { groups: next, removed };
};

/** The values of one group field that no group holds yet. */
export const unclaimedValues = (fields: FieldMap, key: StoreFieldKey, groups: PlanGroupConfig[], dim: number): string[] => {
  const held = new Set(groups.flatMap((g) => (g.match[dim] ?? []).map(fold)));
  return valueOptions(fields, key, groups, dim).filter((v) => !held.has(fold(v)));
};
